import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '../../environments/environment';
import {
  ClientCvTheme,
  CvCurationPayload,
  CvDefaults,
  GeneratedCv,
  Paginated
} from '../interfaces/cv';

/**
 * HTTP client for the CV generator API.
 *
 * URL and credentials pattern mirrors the shared APIService:
 *   - Base URL prefix: environment.api (i.e. '/api')
 *   - All requests send cookies via withCredentials: true
 *
 * Methods return Observables directly so callers can compose with RxJS
 * operators and the Angular async pipe. Error handling and toast/redirect
 * behaviour that APIService provides is intentionally left to the caller
 * since this service is consumed by dedicated CV components.
 */
@Injectable({
  providedIn: 'root'
})
export class CvService {

  private readonly httpOptions = {
    withCredentials: true
  };

  constructor(private http: HttpClient) {}

  /**
   * Fetch the defaults payload used to seed the generation side panel:
   * subject user snapshot, client options, templates, locales, theme, and
   * a pre-selected curation payload.
   */
  public getDefaults(
    userId: number,
    params: { client_id?: number; template_id?: string; locale?: string; for?: string } = {}
  ): Observable<CvDefaults> {
    let httpParams = new HttpParams();
    if (params.client_id !== undefined && params.client_id !== null) {
      httpParams = httpParams.set('client_id', params.client_id.toString());
    }
    if (params.template_id) {
      httpParams = httpParams.set('template_id', params.template_id);
    }
    if (params.locale) {
      httpParams = httpParams.set('locale', params.locale);
    }
    if (params.for) {
      httpParams = httpParams.set('for', params.for);
    }
    return this.http.get<CvDefaults>(
      `${env.api}/users/${userId}/cv/defaults`,
      { ...this.httpOptions, params: httpParams }
    );
  }

  /**
   * Render a preview of the CV without persisting anything.
   * Returns the raw HTML document as a string.
   */
  public preview(userId: number, payload: CvCurationPayload): Observable<string> {
    return this.http.post(
      `${env.api}/users/${userId}/cv/preview`,
      payload,
      { ...this.httpOptions, responseType: 'text' }
    );
  }

  /**
   * Generate and persist a CV PDF. Returns the new GeneratedCv id and a
   * signed download URL.
   */
  public generate(
    userId: number,
    payload: CvCurationPayload
  ): Observable<{ id: number; download_url: string }> {
    return this.http.post<{ id: number; download_url: string }>(
      `${env.api}/users/${userId}/cv/generate`,
      payload,
      this.httpOptions
    );
  }

  /**
   * List generated CVs for a subject user.
   */
  public listForUser(userId: number, page: number = 1): Observable<Paginated<GeneratedCv>> {
    const params = new HttpParams().set('page', page.toString());
    return this.http.get<Paginated<GeneratedCv>>(
      `${env.api}/users/${userId}/cv`,
      { ...this.httpOptions, params }
    );
  }

  /**
   * List generated CVs for a client.
   */
  public listForClient(clientId: number, page: number = 1): Observable<Paginated<GeneratedCv>> {
    const params = new HttpParams().set('page', page.toString());
    return this.http.get<Paginated<GeneratedCv>>(
      `${env.api}/clients/${clientId}/cv`,
      { ...this.httpOptions, params }
    );
  }

  /**
   * Search/sort/filter generated CVs (bst-dual-axis surface). Backed by
   * the GeneratedCvQueryBuilder on the server; mirrors the request shape
   * the other bst-dual-axis delegates send (projects/users/clients).
   */
  public find(payload: {
    scope: 'user' | 'client';
    scopeId: number;
    filters?: { [key: string]: any };
    sorts?: { col: string; dir: number }[];
    page?: number;
  }): Observable<Paginated<GeneratedCv>> {
    return this.http.post<Paginated<GeneratedCv>>(
      `${env.api}/cv/find`,
      payload,
      this.httpOptions
    );
  }

  /**
   * Fetch a single generated CV record.
   */
  public show(cvId: number): Observable<GeneratedCv> {
    return this.http.get<GeneratedCv>(
      `${env.api}/cv/${cvId}`,
      this.httpOptions
    );
  }

  /**
   * Delete a generated CV.
   */
  public delete(cvId: number): Observable<void> {
    return this.http.delete<void>(
      `${env.api}/cv/${cvId}`,
      this.httpOptions
    );
  }

  /**
   * URL for the inline-disposition preview of a generated CV. Suitable for
   * window.open so the browser renders the PDF in a new tab rather than
   * dropping it into Downloads (which is what /download does).
   */
  public previewUrl(cvId: number): string {
    return `${env.api}/cv/${cvId}/preview`;
  }

  /**
   * Fetch the CV theme for a client, or null if none is configured.
   */
  public getTheme(clientId: number): Observable<ClientCvTheme | null> {
    return this.http.get<ClientCvTheme | null>(
      `${env.api}/clients/${clientId}/cv-theme`,
      this.httpOptions
    );
  }

  /**
   * Create or update the CV theme for a client.
   */
  public updateTheme(
    clientId: number,
    body: Partial<ClientCvTheme>
  ): Observable<ClientCvTheme> {
    return this.http.put<ClientCvTheme>(
      `${env.api}/clients/${clientId}/cv-theme`,
      body,
      this.httpOptions
    );
  }
}
